import imageCompression from 'browser-image-compression';

/**
 * Compression presets for different upload contexts.
 * All presets output JPEG at 80% quality via Web Workers.
 */
const PRESETS = {
  jobImage: {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.8,
  },
  avatar: {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 800,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.8,
  },
  document: {
    maxSizeMB: 1.5,
    maxWidthOrHeight: 2048,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.85,
  },
};

/** Minimum file size (500KB) below which we skip compression. */
const SKIP_THRESHOLD = 500 * 1024;

/**
 * Compress an image file before uploading.
 *
 * @param {File} file - The original file selected by the user.
 * @param {string} [preset='jobImage'] - One of 'jobImage', 'avatar', 'document'.
 * @returns {Promise<File>} The compressed file, or the original if compression
 *   is skipped or fails.
 */
export async function compressImage(file, preset = 'jobImage') {
  // Skip non-image files (e.g. PDFs)
  if (!file.type.startsWith('image/') && file.type !== '' && file.type !== 'application/octet-stream') {
    return file;
  }

  // Skip small files that are already under the threshold
  if (file.size <= SKIP_THRESHOLD) {
    console.log(`[compress] Skipping ${file.name} (${(file.size / 1024).toFixed(0)}KB < ${SKIP_THRESHOLD / 1024}KB threshold)`);
    return file;
  }

  const options = PRESETS[preset] || PRESETS.jobImage;

  try {
    const compressed = await imageCompression(file, options);
    const ratio = ((1 - compressed.size / file.size) * 100).toFixed(0);
    console.log(
      `[compress] ${file.name}: ${(file.size / 1024 / 1024).toFixed(1)}MB -> ${(compressed.size / 1024 / 1024).toFixed(1)}MB (${ratio}% reduction)`
    );
    return compressed;
  } catch (err) {
    console.warn('[compress] Compression failed, using original file:', err);
    return file;
  }
}

export default compressImage;
